import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Chip,
  Paper,
} from "@mui/material";
import { connect } from "react-redux";
import {
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  MainInput,
  centered_flex_box,
  left_flex_box,
  main_button,
} from "../app/components/Styles";
import { isMobile } from "react-device-detect";
import { Image, notification, Space, Table, Tag } from "antd";
import Logo from "../app/components/logo.png";
import PropTypes from "prop-types";
import MuiPhoneNumber from "material-ui-phone-number";
import ImageComponent from "../app/components/ImageComponent";
import { addOrderScratch, getProducts } from "../app/store/actions/dataActions";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { calculateOrderPrice } from "../utils/orderPrice";
import { AddCircleOutline, Search, Close } from "@mui/icons-material";
import moment from "moment";
import cities from "../constants/cities.json";
import { FincartAreaField } from "../app/components/FincartAreaField";
import { isAfterSameDayCutoff } from "../utils/sameDay";

const hasValidPhone = (phone = "") => /^\+201\d{9}$/.test(phone.replace(/\s+/g, ""));

export const ScratchOrder = ({
  user,
  order,
  products,
  addOrderScratch,
  getProducts,
  isLoading,
  isWaiting
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [outgoingItems, setOutgoingItems] = useState([]);
  const [outgoingItemsMap, setOutgoingItemsMap] = useState(new Map()); // Map of _id -> product data
  const [price, setPrice] = useState(0);
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState({
    customer: {
      first_name: "",
      last_name: "",
    },
    phone: "",
    shipping_address: {
      address1: "",
      address2: "",
      province: "Cairo",
      area: "",
    },
    shopifyOrderNumber: "",
    sdd: false,
  });

  const canSubmit = () => {
    return (
      formData.customer.first_name &&
      formData.customer.last_name &&
      hasValidPhone(formData.phone) &&
      formData.shipping_address.address1 &&
      formData.shipping_address.province &&
      outgoingItems.length > 0
    );
  };

  useEffect(() => {
    if (priceManuallyEdited) return;
    setPrice(calculateOrderPrice(outgoingItems, outgoingItemsMap, products?.docs));
  }, [outgoingItems, outgoingItemsMap, products?.docs, priceManuallyEdited]);

  useEffect(() => {
    const params = {
      page,
      limit: pageSize,
      search: search || undefined,
    };
    getProducts(params);
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    const params = {
      page: 1,
      limit: pageSize,
      search: search || undefined,
    };
    getProducts(params);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
    const params = {
      page: 1,
      limit: pageSize,
    };
    getProducts(params);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleSubmit = () => {
    if(!canSubmit()) {
      notification.error({
        message: hasValidPhone(formData.phone)
          ? t("fulfillment.errors.requiredFields")
          : t("fulfillment.errors.phoneFormat"),
      });
      return;
    }
    console.log({ orderItems: outgoingItems, price, ...formData })
    addOrderScratch({ orderItems: outgoingItems, price, ...formData }, navigate)
  }

  const returnColumns = [
    {
      title: t("common.sku"),
      dataIndex: 'externalId',
    },
    {
      title: t("common.name"),
      dataIndex: 'name',
    },
    {
      title: t("common.image"),
      dataIndex: "image",
      render: (text) => <Image src={text} width={50} height={50} />
    },
    {
      title: t("common.price"),
      dataIndex: 'price',
      render: (text) => `${text} EGP`,
    },
    {
      title: t("common.size"),
      dataIndex: 'size',
    },
    {
      title: t("duplicateOrder.qtyAvailable"),
      dataIndex: 'onHand',
      sorter: (a, b) => a.onHand - b.onHand,
    },
    {
      title: t("duplicateOrder.qtyOnHand"),
      dataIndex: 'amount',
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: t("duplicateOrder.qtyToSend"),
      render: (_, { _id }) => <Space>
        <MainInput
          margin="normal"
          type="number"
          fullWidth
          id={_id}
          disabled={outgoingItems.filter((item) => item._id === _id).length === 0}
          inputProps={{
            min: outgoingItems.filter((item) => item._id === _id).length > 0 ? 1 : 0
          }}
          value={outgoingItems.filter((item) => item._id === _id).length > 0 ? outgoingItems.filter((item) => item._id === _id)[0].quantity : 0}
          onChange={({ target }) => onQuantityChange(_id, target.value)}
        />
      </Space>
    },
  ];

  const returnData = products?.docs?.map((row) => {
    return {
      _id: row._id,
      key: row._id,
      externalId: row.sku || row.externalId,
      price: row.price,
      image: row.image,
      name: row.name,
      size: row.size,
      color: row.color,
      weight: row.weight,
      unpackedPieces: row.unpackedPieces,
      onHand: row.stocks?.reduce((a, b) => a + b.amount, 0) + row.unpackedPieces,
      amount: row.stocks?.reduce((a, b) => a + b.amount, 0),
    }
  })

  const onQuantityChange = (_id, newQuantity) => {
    const newItems = outgoingItems.map((item) => {
      if (item._id === _id) {
        return { ...item, quantity: parseInt(newQuantity) || 1 };
      }
      return item
    });
    setOutgoingItems(newItems);
  };

  const removeOutgoingItem = (_id) => {
    setOutgoingItems(outgoingItems.filter(item => item._id !== _id));
    const newMap = new Map(outgoingItemsMap);
    newMap.delete(_id);
    setOutgoingItemsMap(newMap);
  };

  // Get current page's row IDs
  const currentPageOutgoingIds = returnData?.map(row => row._id) || [];

  const onSelectChange = (newSelectedRowKeys) => {
    // Get current selections (excluding items from current page)
    const existingSelections = outgoingItems.filter(item => !currentPageOutgoingIds.includes(item._id));
    
    // Get newly selected items from current page
    const newSelections = newSelectedRowKeys
      .filter(key => currentPageOutgoingIds.includes(key))
      .map((key) => {
        const existing = outgoingItems.find(item => item._id === key);
        return existing || { _id: key, quantity: 1 };
      });
    
    // Merge: keep existing + new/updated selections
    const merged = [...existingSelections, ...newSelections];
    
    // Update product map for display
    const newMap = new Map(outgoingItemsMap);
    products?.docs?.forEach(product => {
      if (newSelectedRowKeys.includes(product._id)) {
        newMap.set(product._id, product);
      } else if (currentPageOutgoingIds.includes(product._id)) {
        newMap.delete(product._id);
      }
    });
    
    setOutgoingItems(merged);
    setOutgoingItemsMap(newMap);
  };

  const rowSelection = {
    selectedRowKeys: outgoingItems
      .filter(item => currentPageOutgoingIds.includes(item._id))
      .map(item => item._id),
    onChange: onSelectChange,
  };

  return (
    <div>
      <Box sx={{ width: "100%", padding: 5, mt: 2 }}>
        <Box sx={{ ...centered_flex_box, mb: 1 }}>
          <Typography variant='h4' sx={{ color: "var(--secColor4)" }}>{t("orders.addExternalOrder")}</Typography>
          {/* <IconButton onClick={() => navigate("/ims/admin/products/add")} sx={{ color: "var(--secColor)", mx: 2 }}><AddCircleOutline /></IconButton> */}
        </Box>
        <Box sx={{}}>
          <Box sx={{ ...left_flex_box }}>
            <Grid container sx={{}}>
              <Grid item xs={12}>
                <Typography variant='h6'>{t("fulfillment.customerInformation")}</Typography>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("fulfillment.firstName")}
                      required
                      value={formData.customer.first_name}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, customer: { ...formData.customer, first_name: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("fulfillment.lastName")}
                      required
                      value={formData.customer.last_name}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, customer: { ...formData.customer, last_name: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MuiPhoneNumber
                      sx={{
                        "& label": {
                          color: "var(--secColor4) !important",
                        },
                        "& label.Mui-focused": {
                          color: "var(--secColor4) !important",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "green",
                        },
                        "& .MuiInputBase-input": {
                          outerHeight: 500,
                          color: "var(--secColor4)",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "1px solid var(--secColor4)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "var(--terColor)",
                          },

                          borderRadius: 0,
                        }
                      }}
                      margin="normal"
                      fullWidth
                      required
                      label={t("fulfillment.phoneNumber")}
                      variant='outlined'
                      onlyCountries={['eg']}
                      defaultCountry={'eg'}
                      countryCodeEditable={false}
                      inputProps={{
                        minLength: 13
                      }}
                      disableAreaCodes
                      value={formData.phone}
                      onChange={(value) => setFormData({ ...formData, phone: value })}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h6'>{t("fulfillment.shippingInformation")}</Typography>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={5}>
                    <MainInput
                      margin="normal"
                      label={t("orderDetails.addressLine1")}
                      required
                      value={formData.shipping_address.address1}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, address1: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <MainInput
                      margin="normal"
                      label={t("orderDetails.addressLine2")}
                      value={formData.shipping_address.address2}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, address2: target.value } })}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <MainInput
                      margin="normal"
                      label={t("fulfillment.province")}
                      required
                      value={formData.shipping_address.province}
                      fullWidth
                      select
                      onChange={({ target }) => setFormData({
                        ...formData,
                        shipping_address: { ...formData.shipping_address, province: target.value, area: "" },
                      })}
                    >
                      {cities.map((city) => <MenuItem key={city} value={city}>{city}</MenuItem>)}
                    </MainInput>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.sdd)}
                          onChange={({ target }) => setFormData({ ...formData, sdd: target.checked })}
                        />
                      }
                      label={t("orders.sameDayDelivery")}
                    />
                    <Typography variant="body2" sx={{ color: "var(--secColor)" }}>
                      {t("orders.sameDayDeliveryHelp")}
                    </Typography>
                    {Boolean(formData.sdd) && isAfterSameDayCutoff() && (
                      <Typography variant="body2" sx={{ color: "var(--secColor4)", mt: 0.5 }}>
                        {t("orders.sameDayAfterCutoffWarning")}
                      </Typography>
                    )}
                  </Grid>
                  {user?.vendor?.shippingService === "Fincart" && (
                    <Grid item xs={12}>
                      <FincartAreaField
                        shippingService={user?.vendor?.shippingService}
                        city={formData.shipping_address.province}
                        area={formData.shipping_address.area}
                        onChange={({ target }) => setFormData({ ...formData, shipping_address: { ...formData.shipping_address, area: target.value } })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h6'>{t("orderDetails.orderInformation")}</Typography>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      label={t("orders.orderNumber")}
                      placeholder="#XXXX"
                      value={formData.shopifyOrderNumber}
                      fullWidth
                      onChange={({ target }) => setFormData({ ...formData, shopifyOrderNumber: target.value })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MainInput
                      margin="normal"
                      type="number"
                      label={t("common.price")}
                      value={price}
                      fullWidth
                      onChange={({ target }) => {
                        setPriceManuallyEdited(true);
                        setPrice(target.value);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button sx={{ ...main_button }} onClick={handleSubmit} disabled={isWaiting || isLoading}>
                  {isWaiting ? t("duplicateOrder.sending") : isLoading ? t("scratchOrder.fetchingProducts") : t("duplicateOrder.sendOrder")}
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          {/* Selected Items Display */}
          {outgoingItems.length > 0 && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: 'var(--cardBg)', border: '1px solid var(--borderColor)' }}>
                <Typography variant='h6' sx={{ mb: 2, color: "var(--secColor4)" }}>{t("duplicateOrder.selectedItems", { count: outgoingItems.length })}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {outgoingItems.map((item) => {
                    const product = outgoingItemsMap.get(item._id) || products?.docs?.find(p => p._id === item._id);
                    if (!product) return null;
                    return (
                      <Chip
                        key={item._id}
                        label={`${product.sku || product.externalId} (${t("orderDetails.quantity")}: ${item.quantity})${product.size ? ` - ${product.size}` : ''}`}
                        onDelete={() => removeOutgoingItem(item._id)}
                        deleteIcon={<Close />}
                        sx={{ 
                          backgroundColor: 'var(--tableRowBg)', 
                          border: '1px solid var(--borderColor)',
                          color: 'var(--textPrimary)',
                          '& .MuiChip-deleteIcon': {
                            color: 'var(--textSecondary)',
                            '&:hover': {
                              color: 'var(--primaryColor)'
                            }
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography textAlign={"center"}>{t("orderDetails.orderItems")}</Typography>
              
              {/* Search Bar */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <MainInput
                  margin="normal"
                  placeholder={t("returnsSystem.manageProducts.searchPlaceholder")}
                  value={search}
                  onChange={({ target }) => setSearch(target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {search && (
                          <IconButton
                            onClick={handleClearSearch}
                            edge="end"
                            sx={{ color: "var(--secColor4)" }}
                          >
                            ×
                          </IconButton>
                        )}
                        <IconButton
                          onClick={handleSearch}
                          edge="end"
                          sx={{ color: "var(--secColor4)" }}
                        >
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: '100%', maxWidth: 400 }}
                />
              </Box>
              
              <Table
                columns={returnColumns}
                dataSource={returnData}
                showSorterTooltip={{
                  target: 'sorter-icon',
                }}
                loading={isLoading}
                rowSelection={rowSelection}
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: products?.totalDocs,
                  showSizeChanger: true,
                  showQuickJumper: false,
                  showTotal: (total) => `${t('table.total')} ${total} ${t('table.items')}`,
                }}
                onChange={handleTableChange}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  isWaiting: state?.wait?.isLoading,
  products: state?.records?.products,
  token: state?.auth?.token,
  order: state?.records?.order
});

const mapDispatchToProps = { getProducts, addOrderScratch };

export default connect(mapStateToProps, mapDispatchToProps)(ScratchOrder);
